import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode
} from 'react'

import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox'
import { tv } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Button, type ButtonProps } from '../../atoms/button/button'
import { Divider } from '../../atoms/divider/divider'
import { Icon } from '../../atoms/icon/icon'

export type ComboboxSize = 'sm' | 'md' | 'lg'

export interface ComboboxOption {
  value: string
  label: string
  /**
   * Secondary text shown in parentheses after the label and read as part of
   * the option's name, so options that share a label can be told apart.
   * Search matches the label only.
   */
  description?: string
  group?: string
  icon?: ReactNode
  disabled?: boolean
}

export interface ComboboxAction {
  label: string
  onClick: ButtonProps['onClick']
  disabled?: boolean
  /**
   * `secondary` (default) fills the footer; `tertiary` sits at the start of
   * a subtle footer under a section divider, for a "+ Create new …" affordance.
   */
  variant?: 'secondary' | 'tertiary'
  /** Material symbol rendered before the label. */
  icon?: string
  /** Defaults to the panel's size. */
  size?: ButtonProps['size']
}

type SelectionProps =
  | ({ multiple?: false } & Pick<
      ComboboxPrimitive.Root.Props<string, false>,
      'value' | 'defaultValue' | 'onValueChange'
    >)
  | ({ multiple: true } & Pick<
      ComboboxPrimitive.Root.Props<string, true>,
      'value' | 'defaultValue' | 'onValueChange'
    >)

interface ComboboxPanelProps
  extends Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'defaultValue'> {
  /** Unique values identify options, including across groups and searches. */
  options: readonly ComboboxOption[]
  /** External search text; the panel does not render an input or a trigger. */
  query?: string
  size?: ComboboxSize
  disabled?: boolean
  /** Remains visible while searching, including when no options match. */
  action?: ComboboxAction
  listClassName?: string
}

export type ComboboxProps = ComboboxPanelProps & SelectionProps

function SelectionRoot(
  props: SelectionProps &
    Pick<
      ComboboxPrimitive.Root.Props<string>,
      'children' | 'disabled' | 'inputValue' | 'items' | 'filteredItems'
    >
) {
  return props.multiple ? (
    <ComboboxPrimitive.Root<string, true>
      {...props}
      inline
      open
      loopFocus={false}
      filter={null}
    />
  ) : (
    <ComboboxPrimitive.Root<string, false>
      {...props}
      inline
      open
      loopFocus={false}
      filter={null}
    />
  )
}

const comboboxVariants = tv({
  slots: {
    panel:
      'font-body bg-surface shadow-flat ring-surface-300 flex w-full min-w-0 flex-col overflow-hidden ring-1 ring-inset',
    list: [
      'm-2 mb-4 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain outline-none',
      '[&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-transparent',
      '[&::-webkit-scrollbar-thumb]:bg-surface-400 [&::-webkit-scrollbar-button]:hidden [&::-webkit-scrollbar-thumb]:rounded-full',
      '[@supports_not_selector(::-webkit-scrollbar)]:[scrollbar-color:var(--color-surface-400)_transparent] [@supports_not_selector(::-webkit-scrollbar)]:[scrollbar-width:thin]',
      'focus-visible:outline-primary-200 focus-visible:outline-2 focus-visible:-outline-offset-2'
    ],
    item: [
      'relative flex min-w-0 cursor-pointer select-none items-center gap-2 rounded-sm outline-none',
      'data-highlighted:bg-primary-50 data-selected:bg-primary-50',
      'data-selected:font-semibold data-disabled:cursor-not-allowed data-disabled:opacity-50'
    ],
    icon: 'flex w-6 shrink-0 items-center justify-center',
    footer: 'border-surface-200 shrink-0 border-t p-2 pt-[7px]'
  },
  variants: {
    size: {
      sm: {
        panel: 'text-control-sm rounded-button-sm max-h-[248px]',
        item: 'gap-0.5',
        icon: 'h-[18px]',
        footer: 'pb-0'
      },
      md: {
        panel: 'text-control-md rounded-button-md max-h-[269px]',
        icon: 'h-[21px]'
      },
      lg: {
        panel: 'text-control-lg rounded-button-lg max-h-[292px]',
        icon: 'h-6'
      }
    }
  },
  defaultVariants: { size: 'md' }
})

interface OptionGroup {
  label: string | undefined
  options: ComboboxOption[]
}

function groupOptions(options: readonly ComboboxOption[]): OptionGroup[] {
  const groups = new Map<string | undefined, OptionGroup>()
  for (const option of options) {
    const label = option.group || undefined
    const group = groups.get(label)
    if (group) {
      group.options.push(option)
    } else {
      groups.set(label, { label, options: [option] })
    }
  }
  return Array.from(groups.values())
}

function HighlightedLabel({
  label,
  matcher
}: {
  label: string
  matcher: RegExp | undefined
}) {
  if (!matcher) return label

  return label.split(matcher).map((part, index) =>
    index % 2 === 1 ? (
      <mark key={index} className='bg-yellow-submarine text-inherit'>
        {part}
      </mark>
    ) : (
      part
    )
  )
}

function Option({
  option,
  matcher,
  disabled,
  size
}: {
  option: ComboboxOption
  matcher: RegExp | undefined
  disabled: boolean
  size: ComboboxSize
}) {
  const styles = comboboxVariants({ size })

  return (
    <ComboboxPrimitive.Item
      data-slot='combobox-item'
      value={option.value}
      disabled={disabled || option.disabled}
      // The description's gap is a margin, which the computed name drops.
      aria-label={
        option.description
          ? `${option.label} (${option.description})`
          : undefined
      }
      className={styles.item()}
    >
      {option.icon != null && (
        <span
          data-slot='combobox-icon'
          aria-hidden='true'
          className={styles.icon()}
        >
          {option.icon}
        </span>
      )}
      <span className='min-w-0 flex-1 truncate'>
        <HighlightedLabel label={option.label} matcher={matcher} />
        {option.description && (
          <span
            data-slot='combobox-description'
            className='text-caption-lg text-surface-body ml-1 font-normal'
          >
            ({option.description})
          </span>
        )}
      </span>
      <ComboboxPrimitive.ItemIndicator
        className='flex shrink-0 items-center'
        aria-hidden='true'
      >
        <Icon name='check' />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  )
}

function OptionsGroup({
  group,
  matcher,
  disabled,
  size
}: {
  group: OptionGroup
  matcher: RegExp | undefined
  disabled: boolean
  size: ComboboxSize
}) {
  const children = group.options.map(option => (
    <Option
      key={option.value}
      option={option}
      matcher={matcher}
      disabled={disabled}
      size={size}
    />
  ))

  if (!group.label) {
    return <div className='not-first:mt-4 flex flex-col gap-2'>{children}</div>
  }

  return (
    <ComboboxPrimitive.Group
      data-slot='combobox-group'
      className='not-first:mt-4'
    >
      <ComboboxPrimitive.GroupLabel
        data-slot='combobox-group-label'
        className='text-surface-body mb-1 text-[10px]/[15px] font-semibold'
      >
        {group.label}
      </ComboboxPrimitive.GroupLabel>
      <div className='flex flex-col gap-2'>{children}</div>
    </ComboboxPrimitive.Group>
  )
}

/** Inline Ømni options panel. The owning screen supplies search and positioning. */
export const Combobox = forwardRef<HTMLDivElement, ComboboxProps>(
  (
    {
      options,
      query = '',
      size = 'md',
      disabled = false,
      action,
      className,
      listClassName,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      multiple,
      value,
      defaultValue,
      onValueChange,
      ...props
    },
    ref
  ) => {
    const styles = comboboxVariants({ size })
    const search = query.trim()
    const matcher = search
      ? new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'iu')
      : undefined
    const filteredOptions = options.filter(
      option => !matcher || matcher.test(option.label)
    )
    const groups = groupOptions(filteredOptions)

    const selection: SelectionProps = multiple
      ? { multiple, value, defaultValue, onValueChange }
      : { multiple, value, defaultValue, onValueChange }

    return (
      <SelectionRoot
        {...selection}
        disabled={disabled}
        inputValue={query}
        items={options.map(option => option.value)}
        filteredItems={groups.flatMap(group =>
          group.options.map(option => option.value)
        )}
      >
        <div
          {...props}
          ref={ref}
          data-slot='combobox'
          data-size={size}
          data-disabled={disabled ? '' : undefined}
          className={cn(styles.panel(), 'text-surface-title-50', className)}
        >
          <ComboboxPrimitive.List
            data-slot='combobox-list'
            aria-label={ariaLabelledBy ? ariaLabel : ariaLabel ?? 'Options'}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            aria-disabled={disabled || undefined}
            tabIndex={disabled || filteredOptions.length === 0 ? -1 : 0}
            className={cn(styles.list(), listClassName)}
            onClick={event => {
              if (!disabled) event.currentTarget.focus({ preventScroll: true })
            }}
            onKeyDown={event => {
              // Base UI handles Enter; a standalone listbox also selects with Space.
              if (event.key === ' ' && !disabled) {
                event.preventDefault()
                event.currentTarget
                  .querySelector<HTMLElement>('[data-highlighted]')
                  ?.click()
              }
            }}
          >
            {groups.map(group => (
              <OptionsGroup
                key={group.label ?? ''}
                group={group}
                matcher={matcher}
                disabled={disabled}
                size={size}
              />
            ))}
          </ComboboxPrimitive.List>
          {action && (
            <div
              data-slot='combobox-footer'
              data-variant={action.variant ?? 'secondary'}
              className={cn(
                styles.footer(),
                action.variant === 'tertiary' &&
                  'bg-surface-subtle relative flex items-center border-t-0 px-2.5 py-2'
              )}
            >
              {action.variant === 'tertiary' && (
                <Divider className='absolute inset-x-0 top-0' />
              )}
              <Button
                variant={action.variant ?? 'secondary'}
                size={action.size ?? size}
                className={action.variant === 'tertiary' ? undefined : 'w-full'}
                disabled={disabled || action.disabled}
                onClick={action.onClick}
              >
                {action.icon && <Icon name={action.icon} aria-hidden />}
                {action.label}
              </Button>
            </div>
          )}
        </div>
      </SelectionRoot>
    )
  }
)
Combobox.displayName = 'Combobox'
